import { getFormattedDate } from 'components/Projects/helpers/functions';
import { IS_TUMOR_FIELD_NAME } from 'components/OrderForm/constants';
import { backendURL } from 'services/backendAPI';
import { GridView, ClearAll, DeleteForeverOutlined } from '@mui/icons-material';
import React from 'react';
import { HISTOWIZ_PRIMARY_COLOR, MEDIUM_GREY } from 'utilities/colors';
import {
  ACTIVE_BLOCKS,
  ALL_BLOCKS,
  DISPOSED_BLOCKS,
} from 'components/BlocksList/constants';
import moment from 'moment';
import XLSX from 'xlsx-js-style';
import { DATE_REQUEST_FORMAT } from 'utilities/dates_and_times';

const getTumorType = (block) => {
  const displayedTumorType = {
    true: 'Positive',
    false: 'Negative',
    null: 'Unknown',
  };
  const tumorType = block.samples[0]?.[IS_TUMOR_FIELD_NAME];

  return displayedTumorType[tumorType];
};

const orderIDsGetter = (samples) => {
  const orderIDs = samples?.map((s) => s.order_id);
  const uniqueOrderIDs = [...new Set(orderIDs)];

  return uniqueOrderIDs.join(', ');
};

export const getBlocksProjectCount = (blocks) => {
  const projects = blocks.map((block) => block.projectName);

  const uniqProjects = [...new Set(projects)];

  return uniqProjects.length;
};

export const getRowData = (blocksData, projects) =>
  blocksData
    .filter((block) => {
      const order = projects
        ? projects
          .flatMap((project) => project.orders)
          .find(({ name }) => name.includes(block.original_sample?.order_id))
        : null;

      return order && order.state !== 'cancelled';
    })
    .map((block) => {
      const projectName = projects
        ? projects.find((project) =>
          project.orders.find((order) =>
            order.name.includes(block.original_sample?.order_id),
          ),
        )?.name
        : '';
      const orderName = orderIDsGetter(block.samples);

      return {
        order: [projectName, block.name],
        name: block.name,
        uuid: block.uuid,
        sampleUUID: block.original_sample?.uuid,
        atHistowiz: !block.samples[0]?.order.ship_back_samples,
        modified: getFormattedDate(block.modified),
        created: getFormattedDate(block.created),
        species: block.species?.name || 'N/A',
        submission: block.submission?.name || 'N/A',
        state: block.ship_back_client_state,
        organ: block.organ?.name,
        tumorType: getTumorType(block),
        controlType: block.samples[0]?.control_type || 'N/A',
        slidesCount: block.num_slides,
        block: { ...block, orderName, projectName },
      };
    });

export const getDataPath = (data) => data.order || null;

const BLOCK_EXPORT_HEADERS = [
  'PROJECT',
  'BLOCK',
  'ORDER ID',
  'SLIDES',
  'STATE',
  'AT HISTOWIZ',
  'SUBMISSION TYPE',
  'ORGAN',
  'SPECIES',
  'TUMOR TYPE',
  'CONTROL TYPE',
  'SUBMISSION DATE',
  'LAST MODIFIED',
];

const toTitleCase = (phrase) =>
  phrase
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// Flattens the tree-shaped block rows into one row per block, sorted by
// project then block name so projects sit together in the exported sheet.
export const getBlocksExportRows = (blocks) =>
  (blocks ?? [])
    .map((row) => ({
      PROJECT: row.block?.projectName ?? row.order?.[0] ?? '',
      BLOCK: row.name ?? row.block?.name ?? '',
      'ORDER ID': row.block?.orderName ?? row.order?.[1] ?? '',
      SLIDES: row.slidesCount,
      STATE: row.state ? toTitleCase(row.state) : '',
      'AT HISTOWIZ': row.atHistowiz ? 'Yes' : 'No',
      'SUBMISSION TYPE': row.submission ?? '',
      ORGAN: row.organ ?? '',
      SPECIES: row.species ?? '',
      'TUMOR TYPE': row.tumorType ?? '',
      'CONTROL TYPE': row.controlType ?? '',
      'SUBMISSION DATE': row.created ?? '',
      'LAST MODIFIED': row.modified ?? '',
    }))
    .sort(
      (a, b) =>
        a.PROJECT.localeCompare(b.PROJECT) || a.BLOCK.localeCompare(b.BLOCK),
    );

export const exportBlocksToXlsx = (blocks, baseName = 'blocks') => {
  const rows = getBlocksExportRows(blocks);

  if (!rows.length) return;

  const worksheet = XLSX.utils.json_to_sheet(rows, {
    header: BLOCK_EXPORT_HEADERS,
  });

  BLOCK_EXPORT_HEADERS.forEach((_, index) => {
    const cell = worksheet[`${String.fromCharCode(65 + index)}1`];
    if (cell) {
      cell.s = {
        font: { bold: true },
        fill: { patternType: 'solid', fgColor: { rgb: 'FFD9D9D9' } },
      };
    }
  });

  worksheet['!cols'] = [
    { wch: 30 },
    { wch: 24 },
    { wch: 24 },
    { wch: 10 },
    { wch: 24 },
    { wch: 12 },
    { wch: 20 },
    { wch: 16 },
    { wch: 16 },
    { wch: 16 },
    { wch: 16 },
    { wch: 18 },
    { wch: 18 },
  ];
  worksheet['!autofilter'] = { ref: worksheet['!ref'] };

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Blocks');

  const fileName = `${baseName}-${moment().format(DATE_REQUEST_FORMAT)}`;
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
};

export const getSingleBlocksMenu = ({ row, samplesGridAPI, mergeSamples }) => {
  const lisaLink = `${backendURL}/lisa/core/block/${row.id}/change/`;
  const selectedSamplesUUIDs = samplesGridAPI
    .getSelectedRows()
    ?.map((sample) => sample.uuid);

  return [
    {
      name: 'Edit Block in LISA',
      action: () => window.open(lisaLink, '_blank'),
    },
    {
      name: 'Add selected samples to this block',
      disabled: !selectedSamplesUUIDs.length,
      action: mergeSamples,
    },
  ];
};

export const getMultiBlocksMenu = ({ mergeBlocks }) => [
  {
    name: 'Merge Blocks',
    action: mergeBlocks,
  },
];

export const getSingeSamplesMenu = ({ createBlock }) => [
  {
    name: 'Create Block from Sample',
    action: createBlock,
  },
];

export const getBlocksDropdownItemColor = (selectedType) => {
  switch (selectedType) {
    case ALL_BLOCKS:
      return HISTOWIZ_PRIMARY_COLOR;
    case ACTIVE_BLOCKS:
      return HISTOWIZ_PRIMARY_COLOR;
    case DISPOSED_BLOCKS:
      return HISTOWIZ_PRIMARY_COLOR;
    default:
      return MEDIUM_GREY;
  }
};

export const getDropdownItemsForBlocksPage = (selectedType) => [
  {
    value: ALL_BLOCKS,
    label: 'All Blocks',
    icon: (
      <ClearAll
        sx={{
          color: getBlocksDropdownItemColor(selectedType),
        }}
      />
    ),
  },
  {
    value: ACTIVE_BLOCKS,
    label: 'Active Blocks',
    icon: (
      <GridView
        sx={{
          color: getBlocksDropdownItemColor(selectedType),
        }}
      />
    ),
  },
  {
    value: DISPOSED_BLOCKS,
    label: 'Disposed Blocks',
    icon: (
      <DeleteForeverOutlined
        sx={{
          color: getBlocksDropdownItemColor(selectedType),
        }}
      />
    ),
  },
  // {
  //   value: CONTRIBUTED_BLOCKS,
  //   label: 'Contributed Blocks',
  //   icon: (
  //     <ApprovalDelegationIcon
  //       color={getDropdownItemColor(selectedType, isDefaultColor)}
  //     />
  //   ),
  // },
];
